import App from '../../core/src/App';
import { ConfigurationTypes } from '../../core/src/const/ConfigurationTypes';
import ConfigManager from '../../core/src/managers/ConfigManager';
import ErrorModel from "../../core/src/model/ErrorModel";
import GameModel from '../../core/src/model/GameModel';
import ConsoleUtil from "../../core/src/utils/ConsoleUtil";
import AbstractVendorLocalConfig from '../vendorConfigs/AbstractVendorLocalConfig';
import Configs from '../vendorConfigs/Configs';
import {
    AuthResponse,
    IConnector,
    InfoResponse,
    IReplayResponse,
    ISettings,
    PlayResponse,
    RecoverResponse,
    ReplyResponse,
    Round,
} from "./FTypes";
import VendorWrapper from '../controllers/VendorWrapper';
import SoundController from '../../../vendor_core/core/src/utils/SoundController';

type TypeOfClassMethod<T, M extends keyof T> = T[M] extends Function ? T[M] : never;
type Unwrap<T> =
	T extends Promise<infer U> ? U :
		T extends (...args: any) => Promise<infer U> ? U :
			T extends (...args: any) => infer U ? U :
				T;

type OriginalInitializationInfo =
	Unwrap<TypeOfClassMethod<IConnector, 'authenticate'>>
	& Unwrap<TypeOfClassMethod<IConnector, 'info'>>;

declare global {
    interface Window {
        connector: {
            create(...args: any[]): Promise<IConnector>;
        };
        clientRgsService: {
            CurrenciesDisplayFormat: {}
        };
    }
}

class FConnector implements IConnector {
    public settings: ISettings;

	protected initializationInfo: OriginalInitializationInfo;
	protected iframe: HTMLIFrameElement;

    private instance: IConnector;

    public async initialize() {
        const settings = {
            provider: "fantasmagames",
            rgs: "fantasmagames",
            // game: "missrainbow",
            // key: "", // Authentication key
            // language: "",
            // server: "https://staging.fantasma.tech",
            // realityCheckInterval: 30
        };

        // TODO: set all of these
        const callbacks = { unmute: () => this.unmuted() };

        // TODO: set all of these
        const theme = { backgroundColor: "black" };

        this.instance = await window.connector.create(
            settings,
            callbacks,
            theme
        );

        ConsoleUtil.green("[FConnector] Initialized");
    }

    public async authenticate(): Promise<AuthResponse> {
        try {
            ConsoleUtil.green("[FConnector] Authenticate start");
            const result = await this.instance.authenticate();
            ConsoleUtil.green("[FConnector] Authenticate result:", result);

            this.initializationInfo = { ...this.initializationInfo, ...result };

            return result;
        } catch (e) {
            ConsoleUtil.error("[FConnect] Authenticate error:", e.message);
        }
    }

    public async info<StateT>(): Promise<InfoResponse<StateT>> {
        try {
            ConsoleUtil.green("[FConnector] Info start");
            const result = await this.instance.info<StateT>();
            Configs.game.parseRTP(result.config.rtp);
            // result.settings.forceLanguage = "en_En";
            ConfigManager.getConfig<AbstractVendorLocalConfig>(ConfigurationTypes.LOCAL).parseLanguage(result);
            ConfigManager.getConfig<AbstractVendorLocalConfig>(ConfigurationTypes.LOCAL).parseConfig(result?.config);
            if((result as any).config?.fsRtp){
                Configs.game.parseRTP(result.config.rtp);
                ConfigManager.getConfig<AbstractVendorLocalConfig>(ConfigurationTypes.LOCAL)
                .parseFsAndNhwRTP(
                    (result as any).config?.fsRtp,
                    (result as any).config?.hnwRtp
                );
            }
            ConsoleUtil.green("[FConnector] Info result:", result);

            this.initializationInfo = {...this.initializationInfo, ...result};
            return result;
        } catch (e) {
            ConsoleUtil.error("[FConnect] Info error: ", e.message);
        }
    }

    public async play<RoundT, StateT>(
        action: string,
        bet?: number,
        params?: any,
        cheat?: string
    ): Promise<PlayResponse<RoundT, StateT>> {
        try {
            ConsoleUtil.green("[FConnector] Play start => " + action);
            const result = await this.instance.play<RoundT, StateT>(
                action,
                bet,
                params,
                cheat
            );
            ConsoleUtil.green("[FConnector] Play result:", result);

            return result;
        } catch (e) {
            if (e.message.includes("INSUFFICIENT_FUNDS")) {
                ErrorModel.errorCode = 57;
                ErrorModel.errorMessage = e.message;
            } else {
                ErrorModel.errorCode = 0;
                ErrorModel.errorMessage = e.message;
            }
        }
    }

    async replay<RoundT, StateT, ConfigT>(
        roundId: string
    ): Promise<ReplyResponse<RoundT, StateT, ConfigT>> {
        try {
            ConsoleUtil.green("[FConnector] Recover start");
            const result = await this.instance.replay<RoundT, StateT, ConfigT>(roundId);
            ConsoleUtil.green("[FConnector] Recover result:", result);
            ConfigManager.getConfig<AbstractVendorLocalConfig>(ConfigurationTypes.LOCAL).parseLanguage(result);
            ConfigManager.getConfig<AbstractVendorLocalConfig>(ConfigurationTypes.LOCAL).parseConfig(result?.config);
            if ((result as any).config?.fsRtp) {
                //@ts-ignore
                Configs.game.parseRTP(result.config?.rtp)
                ConfigManager.getConfig<AbstractVendorLocalConfig>(ConfigurationTypes.LOCAL)
                    .parseFsAndNhwRTP(
                        (result as any).config?.fsRtp,
                        (result as any).config?.hnwRtp
                    );
            }
            return result;
        } catch (e) {
            ConsoleUtil.error("[FConnect] Recover error: ", e.message);
        }
    }

    public async complete(): Promise<Omit<AuthResponse, "currency">> {
        try {
            ConsoleUtil.green("[FConnector] Complete start");
            const result = await this.instance.complete();
            ConsoleUtil.green("[FConnector] Complete result:", result);

            return result;
        } catch (e) {
            ConsoleUtil.error("[FConnect] Complete error: ", e.message);
        }
    }

    public async recover<RoundT, StateT>(
        skipPopup: boolean = true
    ): Promise<RecoverResponse<RoundT, StateT>> {
        try {
            ConsoleUtil.green("[FConnector] Recover start");
            const result = await this.instance.recover<RoundT, StateT>(
                skipPopup
            );
            ConsoleUtil.green("[FConnector] Recover result:", result);

            return result;
        } catch (e) {
            ConsoleUtil.error("[FConnect] Recover error: ", e.message);
        }
    }

    initRealityCheck(
        onOpen: () => void,
        onContinue: () => void,
        onGameHistory: () => void
    ): void {
		this.instance.initRealityCheck(onOpen, onContinue, onGameHistory);
    }

    async exit() {

    }

    gameHistory(
        skipPopup: boolean,
        showWatch: boolean,
        showCallback: (replay: any) => void,
        hideCallback: () => void,
        closeCallback: () => void
    ): Promise<any> {
        throw new Error("Method not implemented.");
    }

	openHistory(): void {
        const gameId: string = GameModel.urlParams.game;
        let language: string = GameModel.lang;
        if (language.includes("-")) {
            language = language.replace("-", "_");
        }

		this.instance.gameHistory(
			false,
			true,
			(replay: IReplayResponse) => {
				ConsoleUtil.log('show replay', replay);
                GameModel.isInReplay = true;

				const round = replay.round as Round<any, any> & {bet: number, win: number, roundId: string};
				const channel = this.initializationInfo?.settings?.channel;
				const channelParam = channel ? `channel=${channel}` : '';

				const roundLink: string = `${this.instance.settings.server}/launch/replay?game=${gameId}&roundId=${round.roundId}&language=${language}&${channelParam}`;

				this.showReplay(roundLink);

                SoundController.howler.mute(true);
                App.instance.paused = true;
			},
			() => {
				ConsoleUtil.log('history hide cb');
                this.hideReplay();

                App.instance.paused = false;

                const shoudRestoreAudio = SoundController.isEffectsEnabled || SoundController.isMusicEnabled;
                SoundController.howler.ctx.resume();
                if (shoudRestoreAudio) {
                    SoundController.howler.mute(false);
                }

                GameModel.isInReplay = false;
			},
			() => {
				ConsoleUtil.log('history close cb');
                GameModel.isReplay = false;
				return null;
			},
		);

		if (!gameId.length) {
			throw new Error('FConnector::openHistory game id is not specified');
		}
	}

    gameLoaded(progress?: number): Promise<void> {
        return this.instance.gameLoaded(progress);
    }

    gameReady(): Promise<void> {
        return this.instance.gameReady();
    }

    muted(): Promise<void> {
        throw new Error("Method not implemented.");
    }

    unmuted(): Promise<void> {
        try {
            ConsoleUtil.green("[FConnector] Set bet to  => ");
            const result = this.instance.unmuted();
            return result;
        } catch (e) {
            ConsoleUtil.error("[FConnect] Play error: ", e.message);
        }
    }

    async setActiveBet(bet: number): Promise<void> {
        try {
            ConsoleUtil.green("[FConnector] Set bet to  => " + bet);
            const result = await this.instance.setActiveBet(bet);
            ConsoleUtil.green("[FConnector] Play result:", result);

            return result;
        } catch (e) {
            ConsoleUtil.error("[FConnect] Play error: ", e.message);
        }
    }

	private showReplay(roundLink: string): void {
		this.iframe = document.createElement('iframe');
		this.iframe.src = roundLink;

		this.iframe.style.border = 'none';
		this.iframe.style.position = 'absolute';
		this.iframe.style.zIndex = '1';
		this.iframe.id = 'replayIframe';
		document.body.appendChild(this.iframe);

        App.instance.stage.visible = false;
        VendorWrapper._onResize(true);
	}

	private hideReplay(): void {
		if (this.iframe) {
			document.body.removeChild(this.iframe);
			this.iframe = undefined;

            GameModel.isReplay = false;
            App.instance.stage.visible = true;
            VendorWrapper._onResize(true);
		}
	}
}

export default new FConnector();
